import { Button } from '../ui/button';
import { TradeMode } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TreadSwitchProps = {
  type: 'buy' | 'sell';
  setType: (type: 'buy' | 'sell') => void;
  tradeMode?: TradeMode;
  setTradeMode?: (mode: TradeMode) => void;
};
export const TreadSwitch = ({ type, setType, tradeMode = 'market', setTradeMode }: TreadSwitchProps) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside so Limit picker behaves like a menu.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const tradeModeLabels: Record<TradeMode, string> = {
    market: t('market-order', 'Market'),
    limit: t('limit-order', 'Limit'),
  };

  return (
    <div className="flex items-center justify-between rounded-t-3.5 border-b border-foreground/4">
      <div className="flex items-center">
        <Button
          variant="ghost"
          onClick={() => setType('buy')}
          className={cn(
            'shadow-none rounded-b-none px-2 hover:bg-transparent border-b-2 border-transparent',
            type === 'buy' ? 'border-foreground' : 'text-foreground/70'
          )}
        >
          {t('buy')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => setType('sell')}
          className={cn(
            'shadow-none rounded-b-none px-2 hover:bg-transparent border-b-2 border-transparent',
            type === 'sell' ? 'border-foreground' : 'text-foreground/70'
          )}
        >
          {t('sell')}
        </Button>
      </div>

      {setTradeMode && (
        <div className="relative" ref={dropdownRef}>
          <Button
            variant="ghost"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="shadow-none rounded-b-none px-2 hover:bg-transparent text-foreground/70 flex items-center gap-1"
          >
            {tradeModeLabels[tradeMode]}
            <svg
              className={cn('w-4 h-4 transition-transform', dropdownOpen && 'rotate-180')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Button>

          {dropdownOpen && (
            <div className="absolute end-0 top-full mt-1 bg-card border border-foreground/20 rounded-2 shadow-lg z-50 min-w-32">
              {(['market', 'limit'] as TradeMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => {
                    setTradeMode(mode);
                    setDropdownOpen(false);
                  }}
                  className={cn(
                    'w-full px-3 py-2 text-start text-sm hover:bg-foreground/5 first:rounded-t-2',
                    tradeMode === mode ? 'bg-foreground/10 font-medium' : ''
                  )}
                >
                  {tradeModeLabels[mode]}
                </button>
              ))}
              {/* Merge/Split placeholders - coming soon */}
              <div className="border-t border-foreground/10" />
              <div
                className="w-full px-3 py-2 text-sm text-foreground/40 cursor-not-allowed flex items-center justify-between"
                title="Coming soon"
                aria-disabled="true"
              >
                <span>{t('merge', 'Merge')}</span>
                <span className="text-[10px] uppercase tracking-wider text-foreground/30">Soon</span>
              </div>
              <div
                className="w-full px-3 py-2 text-sm text-foreground/40 cursor-not-allowed flex items-center justify-between last:rounded-b-2"
                title="Coming soon"
                aria-disabled="true"
              >
                <span>{t('split', 'Split')}</span>
                <span className="text-[10px] uppercase tracking-wider text-foreground/30">Soon</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
